/**
 * PII scrubbing for Sentry events and structured log records (web).
 *
 * Web and mobile share one key list; `mobile-shared/src/app/sentry.ts` is the mobile
 * copy — keep them in step. Extracted out of `main.tsx` so the scrubber is importable
 * and testable on its own.
 */

import type * as Sentry from "@sentry/react";

/**
 * PII / token property names to strip from Sentry events (lowercase comparison).
 */
export const SCRUB_KEYS: ReadonlyArray<string> = [
  // Auth / tokens
  "authorization",
  "token",
  "accesstoken",
  "refreshtoken",
  "clerk",
  "password",
  "secret",
  "apikey",
  "jwt",
  "bearer",
  // Contact details
  "email",
  "phone",
  // Personal identifiers
  "childname",
  "firstname",
  "lastname",
  "birthdate",
  "dateofbirth",
  // Sensitive child data
  "health",
  "dietary",
  "allergy",
  "allergies",
  "medication",
  "notes",
  // Portfolio narrative (PII about a child — portfolio-documentation.md §8)
  "body",
  // `title` is deliberately broad: it is also a common non-PII key (dialog titles,
  // chart titles), so scrubbing it costs a little diagnostic context. That is the
  // correct trade when the alternative is a child's observation heading leaving the
  // browser.
  "title",
];

/**
 * Key prefixes whose values are scrubbed (case-insensitive).
 */
export const SCRUB_PREFIXES: ReadonlyArray<string> = ["__clerk_"];

/**
 * Deepest nesting level the scrubber descends into. A Sentry event is not deep
 * (serialized non-Error exceptions and batch DTOs sit a handful of levels down), and an
 * unbounded walk over a hostile or huge object turns `beforeSend` into a hang.
 */
export const MAX_SCRUB_DEPTH = 6;

export function shouldScrubKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (SCRUB_KEYS.includes(lower)) return true;
  return SCRUB_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

/**
 * Recursively delete PII-bearing keys from an event object.
 *
 * Depth-capped ({@link MAX_SCRUB_DEPTH}) and cycle-safe. Descends into array elements as
 * well as plain objects, but never into `Error` instances: their `message`/`stack` are
 * handled by Sentry's own serialization, and deleting `message` off an Error is
 * meaningless.
 */
export function scrubKeyedObject(
  obj: Record<string, unknown>,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): void {
  // The visited-mark is taken HERE, after the depth check, not by the caller
  // before it: a node first reached past the cap must stay unmarked, or a later
  // shallower path to the same object (a DAG, not a cycle) would be skipped as
  // "already seen" and never scrubbed.
  if (depth > MAX_SCRUB_DEPTH) return;
  if (seen.has(obj)) return;
  seen.add(obj);

  for (const key of Object.keys(obj)) {
    if (shouldScrubKey(key)) {
      delete obj[key];
      continue;
    }
    const value = obj[key];
    if (value === null || typeof value !== "object") continue;
    if (value instanceof Error) continue;
    scrubKeyedObject(value as Record<string, unknown>, depth + 1, seen);
  }
}

export function isSensitiveHeaderName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    lower === "authorization" ||
    lower === "cookie" ||
    lower === "set-cookie" ||
    lower === "proxy-authorization" ||
    lower === "x-device-credential" ||
    lower.startsWith("x-clerk-")
  );
}

export function scrubHeaders(headers: Record<string, unknown>): void {
  for (const key of Object.keys(headers)) {
    if (isSensitiveHeaderName(key)) {
      delete headers[key];
    }
  }
}

/**
 * The feedback form collects no identity: no name or e-mail field, nothing
 * pre-filled from the scope user. The report carries the message and the
 * opaque user id only.
 */
export const FEEDBACK_IDENTITY_OPTIONS = {
  showName: false,
  showEmail: false,
  isNameRequired: false,
  isEmailRequired: false,
} as const;

/**
 * Reduce `event.user` to the opaque user id. `useSentryUserContext` already sets only
 * the id; this is the backstop for anything else that writes the scope user.
 */
function scrubSentryUser(event: Sentry.Event): void {
  if (!event.user) return;
  const id = event.user.id;
  if (typeof id === "string" || typeof id === "number") {
    event.user = { id };
  } else {
    delete event.user;
  }
}

/**
 * Strip PII / tokens from a Sentry event before it leaves the browser.
 * Wrapped in try/catch so a scrub failure never drops the event (mirror mobile).
 */
export function scrubSentryEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  try {
    scrubSentryUser(event);
    if (event.request) {
      if (event.request.headers && typeof event.request.headers === "object") {
        scrubHeaders(event.request.headers as Record<string, unknown>);
      }
      // Root-level arrays are scrubbed too: a batch DTO posted as a bare array
      // puts `body`/`title` one index down, and skipping arrays here would let
      // them through. `scrubKeyedObject` walks array indices via `Object.keys`.
      if (event.request.data && typeof event.request.data === "object") {
        scrubKeyedObject(event.request.data as Record<string, unknown>);
      }
    }
    if (event.extra && typeof event.extra === "object") {
      scrubKeyedObject(event.extra as Record<string, unknown>);
    }
    if (Array.isArray(event.breadcrumbs)) {
      for (const crumb of event.breadcrumbs) {
        if (crumb && crumb.data && typeof crumb.data === "object") {
          scrubKeyedObject(crumb.data as Record<string, unknown>);
        }
      }
    }
    return event;
  } catch {
    return event;
  }
}

type SentryTransaction = Parameters<
  NonNullable<Sentry.BrowserOptions["beforeSendTransaction"]>
>[0];

/**
 * `beforeSendTransaction` hook: transactions skip `beforeSend` but carry the scope user.
 */
export function scrubSentryTransaction(event: SentryTransaction): SentryTransaction {
  try {
    scrubSentryUser(event);
    return event;
  } catch {
    return event;
  }
}

type SentryLog = Parameters<NonNullable<Sentry.BrowserOptions["beforeSendLog"]>>[0];

const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
const API_KEY_PATTERN = /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]+/g;
const QUERY_PARAM_PATTERN = /([?&;])([^=&;\s#"]+)=([^&;\s#"]*)/g;

/**
 * Redact e-mail-shaped and token-shaped values inside free text. A query parameter is
 * redacted when its name is on the key list. Never introduces a quote, so it is safe to
 * run over serialized JSON.
 */
export function redactPiiText(text: string): string {
  return text
    .replace(BEARER_PATTERN, "Bearer [token]")
    .replace(JWT_PATTERN, "[token]")
    .replace(API_KEY_PATTERN, "[token]")
    .replace(QUERY_PARAM_PATTERN, (match, sep: string, name: string) =>
      shouldScrubKey(name) || isSensitiveHeaderName(name) ? `${sep}${name}=[redacted]` : match
    )
    .replace(EMAIL_PATTERN, "[email]");
}

/**
 * A log attribute key is dropped when the key or any dotted segment of it matches the
 * key list or a sensitive header name: the SDK writes `user.email` from the scope user,
 * and OpenTelemetry-style names such as `http.request.header.authorization` nest the
 * sensitive part.
 */
function shouldScrubLogAttributeKey(key: string): boolean {
  const candidates = [key, ...key.split(".")];
  return candidates.some((part) => shouldScrubKey(part) || isSensitiveHeaderName(part));
}

function scrubLogAttributeValue(value: unknown): unknown {
  if (typeof value === "string") return redactPiiText(value);
  if (value === null || typeof value !== "object") return value;
  // The SDK sends a non-primitive attribute as its JSON string, so a JSON clone is what
  // would leave the browser anyway; scrubbing the clone keeps the caller's object intact.
  const serialized = JSON.stringify(value);
  if (serialized === undefined) return "";
  const clone = JSON.parse(serialized) as Record<string, unknown>;
  if (clone !== null && typeof clone === "object") scrubKeyedObject(clone);
  return redactPiiText(JSON.stringify(clone));
}

/**
 * `beforeSendLog` hook: the structured-log counterpart of {@link scrubSentryEvent} (#1878).
 * Unlike the event scrubber it fails closed: a log record is diagnostic only, so one that
 * cannot be scrubbed is dropped rather than sent raw.
 */
export function scrubSentryLog(log: SentryLog): SentryLog | null {
  try {
    const attributes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(log.attributes ?? {})) {
      if (shouldScrubLogAttributeKey(key)) continue;
      attributes[key] = scrubLogAttributeValue(value);
    }
    return { ...log, message: redactPiiText(String(log.message)), attributes };
  } catch {
    return null;
  }
}
